/**
 * useFeedChannel — subscribes to the Phoenix `feed:<restaurant_id>` topic and pushes new /
 * updated AnomalyCards to the caller. Degrades gracefully to polling when the socket can't
 * connect (CLAUDE.md: "degrade gracefully (polling fallback)").
 */
import { useEffect, useRef, useState } from "react";
import { Socket } from "phoenix";
import type { Channel } from "phoenix";
import { config } from "@/api/config";
import { api } from "@/api/client";
import { getSocketToken } from "@/api/session";
import type {
  AnomalyCard,
  FeedNewAnomalyMessage,
  FeedAnomalyUpdatedMessage,
} from "@/types/contract";

export type ConnectionState = "connecting" | "live" | "polling" | "offline";

interface UseFeedChannelArgs {
  restaurantId: string;
  /** Called when a brand-new anomaly arrives (animate-in at the top). */
  onNew: (card: AnomalyCard) => void;
  /** Called when an existing anomaly is updated (e.g. status changed elsewhere). */
  onUpdate: (card: AnomalyCard) => void;
  /** Polling fallback re-fetches the whole feed; caller merges. */
  onPollSnapshot: (cards: AnomalyCard[]) => void;
}

const POLL_INTERVAL_MS = 15_000;
const CONNECT_TIMEOUT_MS = 6_000;

export function useFeedChannel({
  restaurantId,
  onNew,
  onUpdate,
  onPollSnapshot,
}: UseFeedChannelArgs): ConnectionState {
  const [state, setState] = useState<ConnectionState>("connecting");

  // Keep latest callbacks without re-subscribing.
  const cbs = useRef({ onNew, onUpdate, onPollSnapshot });
  cbs.current = { onNew, onUpdate, onPollSnapshot };

  useEffect(() => {
    let socket: Socket | null = null;
    let channel: Channel | null = null;
    let pollTimer: ReturnType<typeof setInterval> | null = null;
    let connectTimer: ReturnType<typeof setTimeout> | null = null;
    let disposed = false;

    const startPolling = () => {
      if (pollTimer || disposed) return;
      setState((s) => (s === "live" ? s : "polling"));
      const tick = async () => {
        try {
          const cards = await api.getAnomalies({ restaurant_id: restaurantId });
          if (!disposed) {
            cbs.current.onPollSnapshot(cards);
            setState("polling");
          }
        } catch {
          if (!disposed) setState("offline");
        }
      };
      void tick();
      pollTimer = setInterval(tick, POLL_INTERVAL_MS);
    };

    const stopPolling = () => {
      if (pollTimer) {
        clearInterval(pollTimer);
        pollTimer = null;
      }
    };

    // If mocks are forced, there's no socket to talk to — just poll the mock layer.
    if (config.useMocks || !config.wsUrl) {
      startPolling();
      return () => {
        disposed = true;
        stopPolling();
      };
    }

    try {
      // Токен сокета: socket_token из /session (SSO Rover) либо dev-токен из env.
      const token = getSocketToken();
      socket = new Socket(config.wsUrl, {
        params: token ? { token } : {},
      });

      socket.onError(() => {
        if (disposed) return;
        startPolling();
      });
      socket.onClose(() => {
        if (disposed) return;
        startPolling();
      });

      socket.connect();

      channel = socket.channel(`feed:${restaurantId}`, {});

      channel.on("new_anomaly", (msg: FeedNewAnomalyMessage) => {
        cbs.current.onNew(msg.anomaly);
      });
      channel.on("anomaly_updated", (msg: FeedAnomalyUpdatedMessage) => {
        cbs.current.onUpdate(msg.anomaly);
      });

      channel
        .join()
        .receive("ok", () => {
          if (disposed) return;
          if (connectTimer) clearTimeout(connectTimer);
          stopPolling();
          setState("live");
        })
        .receive("error", () => {
          if (disposed) return;
          startPolling();
        })
        .receive("timeout", () => {
          if (disposed) return;
          startPolling();
        });

      // Hard timeout: if neither ok nor error came back, fall back to polling.
      connectTimer = setTimeout(() => {
        if (!disposed) startPolling();
      }, CONNECT_TIMEOUT_MS);
    } catch {
      startPolling();
    }

    return () => {
      disposed = true;
      if (connectTimer) clearTimeout(connectTimer);
      stopPolling();
      if (channel) channel.leave();
      if (socket) socket.disconnect();
    };
  }, [restaurantId]);

  return state;
}
